import { Outlet, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UnifiedSidebar } from './UnifiedSidebar';
import { AIAssistant } from '../ai/AIAssistant';
import { NotificationSystem } from '../notifications/NotificationSystem';
import { SkillAwareCommandPalette } from '../advanced/SkillAwareCommandPalette';
import { FirstRunGate } from './FirstRunGate';
import { Lightbulb } from 'lucide-react';
import { SidebarLayoutProvider } from './SidebarLayoutContext';
import { useSidebarLayout } from './SidebarLayoutContext';
import { TabMonitorSessionProvider, useTabMonitorSession } from '@/context/TabMonitorSessionContext';
import { GuideTourProvider } from '@/context/GuideTourContext';
import { GuideTourModal } from '../ui/GuideTourModal';
import { tabService } from '@/services/tabs';
import { useGlobalShortcuts } from '@/hooks/useGlobalShortcuts';
import { useLogoutConfirmation } from '@/hooks/useLogoutConfirmation';
import { useUIMode } from '@/context/UIModeContext';

function IUILayoutInner() {
  const [showAssistant, setShowAssistant] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [contextualSuggestions, setContextualSuggestions] = useState<any[]>([]);
  const { workspaceSessionId } = useTabMonitorSession();
  const { sidebarWidth, setCollapsed } = useSidebarLayout();
  const navigate = useNavigate();
  const { promptLogout } = useLogoutConfirmation();
  const { uiMode } = useUIMode();

  // Global keyboard shortcuts (Cmd/Ctrl + K/P/E/D/G/,/S/H/Q, etc.).
  // See `useGlobalShortcuts` for the full mapping.
  useGlobalShortcuts({
    onOpenPalette: () => setShowPalette(true),
    onToggleAssistant: () => setShowAssistant((prev) => !prev),
    onToggleSidebar: () => setCollapsed((c) => !c),
    onQuit: () => promptLogout(),
    navigate,
  });

  useEffect(() => {
    // Fetch real insights and suggestions from tabs
    let mounted = true;
    
    async function fetchSuggestions() {
      if (!workspaceSessionId || uiMode === 'invisible') return;
      
      try {
        const [groups, insights] = await Promise.all([
          tabService.getGroupSuggestions(workspaceSessionId).catch(() => []),
          tabService.getProactiveSuggestions().catch(() => [])
        ]);
        
        const mappedSuggestions = [];
        
        if (mounted && groups && groups.length > 0) {
          mappedSuggestions.push(...groups.map(g => ({
            message: `AI found a group of ${g.count} tabs related to "${g.groupName}".`,
            action: {
              label: "Group Tabs",
              onClick: () => {
                if (!workspaceSessionId) return;
                tabService.createTabGroup(g.groupName, g.tabIds, workspaceSessionId)
                  .then(() => {
                    // Update the local state to remove the suggestion once handled
                    setContextualSuggestions(prev => prev.filter(s => s.action?.label !== "Group Tabs" || !s.message.includes(g.groupName)));
                  })
                  .catch(err => console.error("Failed to group tabs:", err));
              }
            }
          })));
        }

        if (mounted && insights && insights.length > 0) {
          mappedSuggestions.push(...insights.map(i => ({
            message: i.description || i.title,
            action: i.actions && i.actions.length > 0 ? {
              label: i.actions[0].label,
              onClick: () => console.log("Insight action:", i.actions![0].label)
            } : undefined
          })));
        }
        // Intentionally no fallback. Only show real AI insights.
        
        if (mounted && mappedSuggestions.length > 0) {
          setContextualSuggestions(mappedSuggestions);
        }
      } catch (err) {
        console.error("Failed to fetch suggestions:", err);
      }
    }
    
    // Fetch immediately
    fetchSuggestions();
    
    return () => {
      mounted = false;
    };
  }, [workspaceSessionId, uiMode]);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-khiimori-bg-primary">
      {/* Skip to main content link for keyboard accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-500 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>
      {/* GenTabs-Inspired Background */}
      <div
        className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,var(--khiimori-glow-violet),transparent),radial-gradient(ellipse_60%_40%_at_90%_10%,var(--khiimori-glow-fuchsia),transparent),radial-gradient(ellipse_50%_30%_at_10%_90%,var(--khiimori-glow-cyan),transparent),linear-gradient(180deg,var(--khiimori-bg-primary)_0%,var(--khiimori-bg-secondary)_50%,var(--khiimori-bg-tertiary)_100%)]"
      />

      {/* Subtle grid pattern overlay */}
      <div
        className="absolute inset-0 -z-10 pointer-events-none bg-[linear-gradient(var(--khiimori-border)_1px,transparent_1px),linear-gradient(90deg,var(--khiimori-border)_1px,transparent_1px)] bg-[size:60px_60px] opacity-[var(--khiimori-grid-opacity)]"
      />

      {/* Unified sidebar navigation */}
      <UnifiedSidebar />

      {/* Main content area */}
      <main id="main-content" className="relative min-h-screen min-w-0 overflow-x-hidden transition-[margin-left] duration-300 ease-out" style={{ marginLeft: sidebarWidth }}>
        <Outlet />
      </main>

      {/* AI Assistant overlay */}
      <AnimatePresence>
        {showAssistant && <AIAssistant onClose={() => setShowAssistant(false)} />}
      </AnimatePresence>

      {/* Skill-aware command palette (Cmd+K) */}
      <SkillAwareCommandPalette
        isOpen={showPalette}
        onClose={() => setShowPalette(false)}
      />

      {/* First-run onboarding (gated by localStorage.khiimori.onboarded) */}
      <FirstRunGate />

      {/* Notification system */}
      <NotificationSystem
        uiMode={uiMode}
        suggestions={contextualSuggestions}
      />

      {/* AI Assistant quick access button (positioned on bottom-right, clear of sidebar profile info) */}
      {uiMode !== 'invisible' && (
        <button
          onClick={() => setShowAssistant(true)}
          data-tour="ai-assistant-btn"
          aria-label="Open AI Assistant"
          title="Open AI Assistant (Ctrl+J)"
          className={`fixed bottom-6 right-6 hidden sm:flex w-12 h-12 rounded-2xl items-center justify-center transition-all duration-300 z-40 hover:scale-105 active:scale-95 ${
            uiMode === 'ambient'
              ? 'bg-khiimori-bg-elevated/50 backdrop-blur-sm border border-khiimori-border opacity-70 hover:opacity-100 shadow-sm'
              : 'shadow-[0_8px_32px_rgba(139,92,246,0.35)] bg-gradient-secondary text-khiimori-text-primary hover:shadow-[0_8px_36px_rgba(139,92,246,0.5)]'
          }`}
        >
          <Lightbulb className={`w-5 h-5 ${uiMode === 'ambient' ? 'text-khiimori-text-secondary' : 'text-khiimori-text-primary'}`} />
        </button>
      )}

      {/* Interactive Application Guide Tour Spotlight Modal */}
      <GuideTourModal />
    </div>
  );
}

export function IUILayout() {
  return (
    <TabMonitorSessionProvider>
      <SidebarLayoutProvider>
        <GuideTourProvider>
          <IUILayoutInner />
        </GuideTourProvider>
      </SidebarLayoutProvider>
    </TabMonitorSessionProvider>
  );
}

